import React, { FC, useState } from 'react';

import { EditIcon, SearchIcon, TrashBinIcon } from '../../../assets/icons/index';
import Button from '../../../components/button/index';
import { TeamMember, Vacation } from '../../../types/team.types';
import { MemberAvatar } from '../member-avatar';
import { countWeekdays } from '../pto.util';
import { formatIsoDate } from '../team-date.util';
import { SelectField } from '../team-field';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const CURRENT_YEAR = new Date().getFullYear();

export type ListViewProps = {
  members: TeamMember[];
  onDeleteVacation: (vacation: Vacation) => void;
  onEditVacation: (vacation: Vacation) => void;
  personFilter: string;
  vacations: Vacation[];
};

// Inclusive [start, end] ISO bounds for the chosen year/month, or null when no period is selected.
const getPeriodBounds = (year: null | number, month: null | number) => {
  if (month === null && year === null) return null;

  const resolvedYear = year ?? CURRENT_YEAR;

  if (month === null) {
    return { end: `${resolvedYear}-12-31`, start: `${resolvedYear}-01-01` };
  }

  const monthEnd = new Date(resolvedYear, month + 1, 0);
  const pad = (value: number) => String(value).padStart(2, '0');

  return {
    end: `${resolvedYear}-${pad(month + 1)}-${pad(monthEnd.getDate())}`,
    start: `${resolvedYear}-${pad(month + 1)}-01`,
  };
};

export const ListView: FC<ListViewProps> = ({
  vacations,
  members,
  personFilter,
  onEditVacation,
  onDeleteVacation,
}) => {
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [search, setSearch] = useState('');

  const memberById = new Map(members.map((member) => [member.id, member]));
  const resolveMember = (vacation: Vacation): TeamMember =>
    vacation.teamMember ?? memberById.get(vacation.teamMemberId)!;

  const years = Array.from(
    new Set([CURRENT_YEAR, ...vacations.map((vacation) => Number(vacation.startDate.slice(0, 4)))]),
  ).sort((a, b) => b - a);

  const period = getPeriodBounds(
    yearFilter === 'all' ? null : Number(yearFilter),
    monthFilter === 'all' ? null : Number(monthFilter),
  );
  const searchTerm = search.trim().toLowerCase();

  const filtered = vacations.filter((vacation) => {
    if (personFilter !== 'all' && vacation.teamMemberId !== personFilter) return false;

    if (period && !(vacation.startDate <= period.end && vacation.endDate >= period.start)) {
      return false;
    }

    if (searchTerm && !resolveMember(vacation).name.toLowerCase().includes(searchTerm)) {
      return false;
    }

    return true;
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3">
          <SearchIcon iconColor="#98A2B3" />
          <input
            className="h-9 w-[200px] bg-transparent text-sm font-[450] text-gray-700 outline-none"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name"
            value={search}
          />
        </div>
        <SelectField
          className="w-[150px]"
          onChange={setMonthFilter}
          options={[
            { label: 'All months', value: 'all' },
            ...MONTHS.map((month, index) => ({ label: month, value: String(index) })),
          ]}
          value={monthFilter}
        />
        <SelectField
          className="w-[110px]"
          onChange={setYearFilter}
          options={[
            { label: 'All years', value: 'all' },
            ...years.map((year) => ({ label: String(year), value: String(year) })),
          ]}
          value={yearFilter}
        />
      </div>

      <div className="w-full overflow-x-auto rounded-lg border border-gray-100 bg-white">
        <table className="w-full min-w-[820px] border-collapse">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs font-[450] text-gray-500">
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Dates</th>
              <th className="px-4 py-3">Working days</th>
              <th className="px-4 py-3">Notes</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td className="px-4 py-8 text-center text-xs font-[450] text-gray-500" colSpan={6}>
                  No vacations match your filters.
                </td>
              </tr>
            ) : (
              filtered.map((vacation) => {
                const member = resolveMember(vacation);

                return (
                  <tr
                    className="border-b border-gray-50 text-xs font-[450] text-gray-700 last:border-b-0"
                    key={vacation.id}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MemberAvatar member={member} size={26} />
                        <span className="font-[550] text-gray-800">{member.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">{member.role}</td>
                    <td className="px-4 py-3">
                      {formatIsoDate(vacation.startDate)} – {formatIsoDate(vacation.endDate)}
                    </td>
                    <td className="px-4 py-3">
                      {countWeekdays(vacation.startDate, vacation.endDate)}
                    </td>
                    <td className="max-w-[240px] truncate px-4 py-3 text-gray-500">
                      {vacation.notes || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          className="flex size-8 items-center justify-center rounded hover:bg-gray-50"
                          onClick={() => onEditVacation(vacation)}
                          styleType="NONE"
                        >
                          <EditIcon className="size-4" iconColor="#475467" />
                        </Button>
                        <Button
                          className="flex size-8 items-center justify-center rounded hover:bg-fireside-50"
                          onClick={() => onDeleteVacation(vacation)}
                          styleType="NONE"
                        >
                          <TrashBinIcon className="size-4" iconColor="#D92D20" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
